import type { ReactNode } from 'react';
import { useEffect, useRef } from 'react';
import { CollapseContent } from './Collapse.styles';

interface ICollapse {
  children: ReactNode;
  isOpen: boolean;
  className?: string;
  fadeDuration?: number;
  collapseDuration?: number;
}

const DEFAULT_FADE_DURATION = 500;
const DEFAULT_COLLAPSE_DURATION = 300;

const setCollapsedStyle = (element: HTMLDivElement) => {
  element.style.height = '0px';
  element.style.opacity = '0';
  element.style.visibility = 'hidden';
  element.style.overflow = 'hidden';
};

const setExpandedStyle = (element: HTMLDivElement) => {
  element.style.height = 'auto';
  element.style.opacity = '1';
  element.style.visibility = 'visible';
  element.style.overflow = 'visible';
};

const collapse = (element: HTMLDivElement) => {
  const contentHeight = element.scrollHeight;

  // Cannot transition from 'auto' to '0px', so we set the height first
  requestAnimationFrame(() => {
    element.style.height = `${contentHeight}px`;
    element.style.overflow = 'hidden';
    requestAnimationFrame(() => {
      element.style.height = '0px';
      element.style.opacity = '0';
    });
  });
};

const expand = (element: HTMLDivElement) => {
  const contentHeight = element.scrollHeight;

  element.style.opacity = '1';
  element.style.visibility = 'visible';
  element.style.height = `${contentHeight}px`;
};

const msToSecString = (ms: number) => {
  return `${ms / 1000}s`;
};

/* 
  HOW IT WORKS:
  Collapse receives isOpen, which triggers collapse and expand transition animations
  When isOpen = false it measures the current height, and transition from current height to 0
  When isOpen = true, it reverts back to the full height
*/
const Collapse = ({
  isOpen,
  children,
  className,
  fadeDuration = DEFAULT_FADE_DURATION,
  collapseDuration = DEFAULT_COLLAPSE_DURATION,
}: ICollapse) => {
  const collapseRef = useRef<HTMLDivElement>(null);
  const measuredRef = useRef<boolean>(false);

  useEffect(() => {
    const element = collapseRef.current;
    if (!element) return;
    // For the first render, we only need to explicitly set the height of component, no need for transition animation
    // This fixes an issue where Collapse wraps a tall component and switches to a shorter component, and reverse
    if (!measuredRef.current) {
      isOpen ? setExpandedStyle(element) : setCollapsedStyle(element);
      measuredRef.current = true;
      return;
    }

    isOpen ? expand(element) : collapse(element);
  }, [isOpen]);

  const onTransitionEnd = () => {
    if (!collapseRef.current) return;
    const element = collapseRef.current;
    // IMPORTANT: This is to prevent the height to lose its value when screen is resized
    if (isOpen) element.style.height = 'auto';
  };

  return (
    <CollapseContent
      ref={collapseRef}
      className={className}
      onTransitionEnd={onTransitionEnd}
      fadeDuration={msToSecString(fadeDuration)}
      collapseDuration={msToSecString(collapseDuration)}
    >
      {children}
    </CollapseContent>
  );
};

export { Collapse };
